/**
 * FuelSalesTab Component
 * Handles fuel sales entry - grades, volumes, prices
 * Prices are READ-ONLY - set by Admin via Fuel Prices page
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { PoundSterling, Fuel, Car, Info, AlertTriangle, Lock } from 'lucide-react';
import { getFuelGradeColor } from '../../../utils/fuelGradeColors';

export const FuelSalesTab = ({
  storeDetails,
  tillLock = false,
  // Sales amounts
  fuelSalesDeptAmount,
  setFuelSalesDeptAmount,
  retailSalesAmount,
  setRetailSalesAmount,
  bunkeringSalesAmount,
  setBunkeringSalesAmount,
  driveOffDeptAmount,
  setDriveOffDeptAmount,
  // Fuel data - prices are now READ-ONLY from admin
  fuelPrices,         // From admin retail prices
  fuelVolumes,
  setFuelVolumes,
  fuelCosts,          // Cost prices for margin calculation
  fuelCardTotals,
  // Calculated values
  calculatedFuelSales,
  incidentDeductions,
  incidentRecoveries,
  // Price source info
  priceSourceDate,    // Date the prices came from
  pricesLoadedFrom,   // 'exact_date' or 'fallback_from_YYYY-MM-DD'
  // Handlers (removed price change handlers - prices are read-only)
  handleAmountFocus,
  isAdmin,            // To show admin-specific info
}) => {
  // Check if prices are loaded
  const hasPrices = fuelPrices && Object.keys(fuelPrices).length > 0;
  
  return (
    <div className="space-y-3">
      {tillLock && (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-blue-500/30 bg-blue-500/10 text-sm text-blue-600 dark:text-blue-400" data-testid="till-lock-banner-fuel">
          <Lock className="h-4 w-4" />
          These figures were filled from the Day End report and are read-only.
        </div>
      )}
      {/* Staff Info Banner - Remind to verify prices */}
      {!isAdmin && (
        <Alert className="bg-blue-500/10 border-blue-500/30">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-sm">
            <span className="font-medium">Please verify</span> that the retail prices shown below match your current pump prices. 
            Contact Admin if prices need to be updated.
            {priceSourceDate && (
              <span className="text-muted-foreground ml-2">
                (Prices from: {priceSourceDate})
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {/* No Prices Warning */}
      {!hasPrices && (
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <span className="font-medium">No retail prices set!</span> Please contact Admin to set prices for this date before entering sales.
          </AlertDescription>
        </Alert>
      )}
      {/* Total Sales, Retail Sales & Bunkering Sales - Compact Row */}
      <Card className="border-amber-500/30">
        <CardContent className="py-3 px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Sales */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-[100px]">
                <PoundSterling className="h-4 w-4 text-green-500" />
                <Label className="text-sm font-medium whitespace-nowrap">
                  Total Sales<span className="text-red-500">*</span>
                </Label>
              </div>
              <div className="relative flex-1 max-w-[120px]">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
                <Input
                  type="text"
                  value={fuelSalesDeptAmount}
                  readOnly={tillLock}
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setFuelSalesDeptAmount(e.target.value);
                    }
                  }}
                  onBlur={() => {
                    const num = parseFloat(fuelSalesDeptAmount);
                    const formatted = isNaN(num) || fuelSalesDeptAmount === '' ? '0.00' : num.toFixed(2);
                    setFuelSalesDeptAmount(formatted);
                  }}
                  onFocus={handleAmountFocus}
                  className="pl-5 h-8 text-sm font-mono text-right"
                  placeholder="0.00"
                  data-testid="fuel-sales-dept-amount"
                />
              </div>
            </div>

            {/* Retail Sales Amount */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-[100px]">
                <Fuel className="h-4 w-4 text-blue-500" />
                <Label className="text-sm font-medium whitespace-nowrap">
                  Retail Sales<span className="text-red-500">*</span>
                </Label>
              </div>
              <div className="relative flex-1 max-w-[120px]">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
                <Input
                  type="text"
                  value={retailSalesAmount}
                  readOnly={tillLock}
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setRetailSalesAmount(e.target.value);
                    }
                  }}
                  onBlur={() => {
                    const num = parseFloat(retailSalesAmount);
                    const formatted = isNaN(num) || retailSalesAmount === '' ? '0.00' : num.toFixed(2);
                    setRetailSalesAmount(formatted);
                  }}
                  onFocus={handleAmountFocus}
                  className="pl-5 h-8 text-sm font-mono text-right"
                  placeholder="0.00"
                  data-testid="retail-sales-amount"
                />
              </div>
              {parseFloat(retailSalesAmount) > 0 && calculatedFuelSales > 0 && (
                <div className={`text-xs px-2 py-1 rounded ${
                  Math.abs(parseFloat(retailSalesAmount) - calculatedFuelSales) < 1 
                    ? 'bg-green-500/20 text-green-600' 
                    : 'bg-yellow-500/20 text-yellow-600'
                }`}>
                  Calc: £{calculatedFuelSales.toFixed(2)}
                </div>
              )}
            </div>

            {/* Bunkering Sales Amount */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-[100px]">
                <Fuel className="h-4 w-4 text-orange-500" />
                <Label className="text-sm font-medium whitespace-nowrap">
                  Bunkering Sales<span className="text-red-500">*</span>
                </Label>
              </div>
              <div className="relative flex-1 max-w-[120px]">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
                <Input
                  type="text"
                  value={bunkeringSalesAmount}
                  readOnly={tillLock}
                  onChange={(e) => {
                    if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setBunkeringSalesAmount(e.target.value);
                    }
                  }}
                  onBlur={() => {
                    const num = parseFloat(bunkeringSalesAmount);
                    const formatted = isNaN(num) || bunkeringSalesAmount === '' ? '0.00' : num.toFixed(2);
                    setBunkeringSalesAmount(formatted);
                  }}
                  onFocus={handleAmountFocus}
                  className="pl-5 h-8 text-sm font-mono text-right"
                  placeholder="0.00"
                  data-testid="bunkering-sales-amount"
                />
              </div>
              {parseFloat(bunkeringSalesAmount) > 0 && fuelCardTotals.bunkering + fuelCardTotals.key_fuels > 0 && (
                <div className={`text-xs px-2 py-1 rounded ${
                  Math.abs(parseFloat(bunkeringSalesAmount) - (fuelCardTotals.bunkering + fuelCardTotals.key_fuels)) < 1 
                    ? 'bg-green-500/20 text-green-600' 
                    : 'bg-yellow-500/20 text-yellow-600'
                }`}>
                  Calc: £{(fuelCardTotals.bunkering + fuelCardTotals.key_fuels).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drive Off - Separate compact row */}
      <Card className="border-red-500/30">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 min-w-[140px]">
              <Car className="h-4 w-4 text-red-500" />
              <Label className="text-sm font-medium whitespace-nowrap">Drive Off / Debt</Label>
            </div>
            <div className="relative flex-1 max-w-[120px]">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">£</span>
              <Input
                type="text"
                value={driveOffDeptAmount}
                onChange={(e) => {
                  if (e.target.value === '' || e.target.value === '-' || /^-?[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                    setDriveOffDeptAmount(e.target.value);
                  }
                }}
                onBlur={() => {
                  const num = parseFloat(driveOffDeptAmount);
                  const formatted = isNaN(num) || driveOffDeptAmount === '' || driveOffDeptAmount === '-' ? '0.00' : num.toFixed(2);
                  setDriveOffDeptAmount(formatted);
                }}
                onFocus={handleAmountFocus}
                className="pl-5 h-8 text-sm font-mono text-right"
                placeholder="0.00"
                data-testid="driveoff-dept-amount"
              />
            </div>
            {(parseFloat(driveOffDeptAmount) !== 0 || incidentDeductions > 0) && (
              <div className={`text-xs px-2 py-1 rounded ${
                Math.abs(parseFloat(driveOffDeptAmount) - (incidentDeductions - incidentRecoveries)) < 0.01 
                  ? 'bg-green-500/20 text-green-600' 
                  : 'bg-yellow-500/20 text-yellow-600'
              }`}>
                Net: £{(incidentDeductions - incidentRecoveries).toFixed(2)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Fuel Volumes Section - Compact */}
      {storeDetails.fuel_grades?.length > 0 && (
        <Card>
          <CardHeader className="bg-amber-500/5 border-b border-amber-500/20 py-2 px-4">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Barlow Condensed' }}>
              <Fuel className="h-4 w-4 text-amber-500" />
              Retail Sales
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4">
            {/* Header row */}
            <div className="grid grid-cols-6 gap-2 text-xs font-medium text-muted-foreground border-b border-border pb-1 mb-2">
              <div>Grade</div>
              <div className="text-right">Cost (ppl)</div>
              <div className="text-right">Price (ppl)</div>
              <div className="text-right">Volume (L)</div>
              <div className="text-right">Margin</div>
              <div className="text-right">Total (£)</div>
            </div>
            <div className="space-y-1">
            {storeDetails.fuel_grades?.map(grade => {
              const cost = fuelCosts[grade.id] || 0;
              const price = parseFloat(fuelPrices[grade.id]) || 0;
              const volume = parseFloat(fuelVolumes[grade.id]) || 0;
              const margin = price > 0 ? (price - cost).toFixed(3) : '-';
              const gradeTotal = (volume * price) / 100; // price is in pence, convert to pounds
              const gradeColors = getFuelGradeColor(grade.name);
              
              return (
                <div key={grade.id} className="grid grid-cols-6 gap-2 items-center">
                  <div className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${gradeColors.bg}`}></span>
                    <Label className="font-medium text-sm truncate">{grade.name}</Label>
                  </div>
                  {/* Cost - Read Only */}
                  <div className="text-right font-mono text-xs text-muted-foreground bg-muted/30 px-2 py-1.5 rounded">
                    {cost > 0 ? cost.toFixed(3) : '-'}
                  </div>
                  {/* Sales Price - READ ONLY (Set by Admin) */}
                  <div className={`text-right font-mono text-sm px-2 py-1.5 rounded ${
                    price > 0 ? 'bg-amber-500/10 text-amber-600 font-medium' : 'bg-muted/30 text-muted-foreground'
                  }`} data-testid={`fuel-price-${grade.id}`}>
                    {price > 0 ? price.toFixed(1) : '-'}
                  </div>
                  {/* Volume */}
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      value={fuelVolumes[grade.id] || '0'}
                  readOnly={tillLock}
                      onChange={(e) => setFuelVolumes({ ...fuelVolumes, [grade.id]: e.target.value })}
                      className="font-mono text-right text-sm h-7 px-2 pr-5"
                      data-testid={`fuel-volume-${grade.id}`}
                      autoComplete="off"
                      disabled={!hasPrices}
                    />
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">L</span>
                  </div>
                  {/* Margin */}
                  <div className={`text-right font-mono text-xs px-2 py-1.5 rounded ${
                    margin !== '-' && parseFloat(margin) > 0 ? 'text-green-400 bg-green-500/10' : 
                    margin !== '-' && parseFloat(margin) < 0 ? 'text-red-400 bg-red-500/10' : 'text-muted-foreground'
                  }`}>
                    {margin !== '-' ? `${margin}p` : '-'}
                  </div>
                  {/* Grade Total (Volume × Price) */}
                  <div className={`text-right font-mono text-sm font-medium px-2 py-1.5 rounded ${
                    gradeTotal > 0 ? 'text-amber-600 bg-amber-500/10' : 'text-muted-foreground'
                  }`}>
                    {gradeTotal > 0 ? `£${gradeTotal.toFixed(2)}` : '-'}
                  </div>
                </div>
              );
            })}
            </div>
            
            {/* Calculated Retail Sales Summary - Compact */}
            <div className="border-t border-border pt-2 mt-2 flex items-center justify-between text-sm">
              <span className="font-medium">Calculated Retail Sales:</span>
              <span className="font-mono font-bold text-base">£{calculatedFuelSales.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FuelSalesTab;
